import {
  useState,
  useCallback,
  useRef,
  useEffect,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react'
import { getModelById } from '../lib/models'
import ModelSelector from './ModelSelector'

interface PromptBarProps {
  prompt: string
  onPromptChange: (value: string) => void
  selectedModelId: string
  onModelChange: (modelId: string) => void
  onGenerate: (prompt: string) => void
  isGenerating: boolean
  disabled?: boolean
  // Fired when the textarea focus state changes — Studio uses this to
  // show/hide the PromptTerminal above the prompt bar.
  onFocusChange?: (focused: boolean) => void
}

export default function PromptBar({
  prompt,
  onPromptChange,
  selectedModelId,
  onModelChange,
  onGenerate,
  isGenerating,
  disabled = false,
  onFocusChange,
}: PromptBarProps) {
  const [showModelPicker, setShowModelPicker] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const pickerRef = useRef<HTMLDivElement>(null)
  const model = getModelById(selectedModelId)

  // Close model picker on outside click
  useEffect(() => {
    if (!showModelPicker) return
    const handleClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowModelPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showModelPicker])

  const handleSubmit = useCallback(() => {
    const trimmed = prompt.trim()
    if (!trimmed || isGenerating || disabled) return
    onGenerate(trimmed)
    onPromptChange('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [prompt, isGenerating, disabled, onGenerate, onPromptChange])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit],
  )

  const handleInput = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onPromptChange(e.target.value)
      const el = e.target
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 80)}px`
    },
    [onPromptChange],
  )

  // When prompt is externally replaced (e.g. "Use prompt" from modal), resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 80)}px`
  }, [prompt])

  const handleModelSelect = useCallback(
    (modelId: string) => {
      onModelChange(modelId)
      setShowModelPicker(false)
    },
    [onModelChange],
  )

  const isDisabled = isGenerating || disabled

  return (
    <div
      className="fixed right-0 left-[var(--sidebar-width)] z-40 bg-bg-secondary/95 backdrop-blur-sm border-t border-border max-md:left-0"
      style={{ bottom: 'var(--terminal-height, 32px)' }}
    >
      <div className="max-w-4xl mx-auto px-4 py-3 space-y-2">
        <div className="flex items-end gap-3">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onFocus={() => onFocusChange?.(true)}
            onBlur={() => onFocusChange?.(false)}
            placeholder="Describe what you want to create..."
            disabled={isDisabled}
            rows={1}
            className="input-glow flex-1 resize-none bg-bg-card border border-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-neon transition-all disabled:opacity-50"
          />
          <button
            onClick={handleSubmit}
            disabled={isDisabled || !prompt.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-neon text-bg-primary font-semibold rounded-xl hover:bg-neon-dim transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shrink-0"
          >
            {isGenerating ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Generating
              </>
            ) : (
              'Generate'
            )}
          </button>
        </div>
        <div className="flex items-center gap-4 text-xs text-text-muted relative">
          <div ref={pickerRef} className="relative">
            <button
              type="button"
              onClick={() => setShowModelPicker((v) => !v)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-bg-elevated transition-colors cursor-pointer"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-neon" />
              <span className="text-text-secondary font-medium">
                {model?.name ?? 'Select model'}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={`transition-transform ${showModelPicker ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {showModelPicker && (
              <div className="absolute bottom-full left-0 mb-2 w-80 max-h-[60vh] overflow-y-auto p-3 rounded-xl bg-bg-card border border-border shadow-2xl shadow-black/50 z-50">
                <ModelSelector
                  selectedModelId={selectedModelId}
                  onChange={handleModelSelect}
                />
              </div>
            )}
          </div>
          {model && (
            <span>
              {model.default_steps} steps · {model.default_width}×{model.default_height}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
